import React, { useEffect, useState } from "react";
import { ShoppingBag, Menu, X, Search, ShieldCheck, UserCog, LayoutDashboard, LogOut } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const links = [
  { href: "#shop", label: "Shop" },
  { href: "#reservations", label: "Reserve" },
  { href: "#wallet", label: "Wallet" },
  { href: "#journal", label: "Journal" },
  { href: "#gallery", label: "Gallery" },
  { href: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const { totals, setOpen } = useCart();
  const { auth, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onSearch = (e) => {
    const v = e.target.value;
    setQuery(v);
    window.dispatchEvent(new CustomEvent("search-products", { detail: v }));
    if (v && v.trim().length > 0) {
      const el = document.getElementById("shop");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-background/85 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-3">
          <span className="relative w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden ring-2 ring-accent/40 shadow-md shrink-0">
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_alternate-you-83/artifacts/i0gmlqe6_VILLAGEFARMFOOD%20LOGO.jpg"
              alt="Village Farm Food logo"
              className="w-full h-full object-cover"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl md:text-2xl font-semibold tracking-tight">
              Village<span className="text-accent">·</span>Farm<span className="text-accent">·</span>Food
            </span>
            <span className="mt-1 font-mono-ui text-[9px] md:text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Direct to Farm · <span className="text-accent">Shuddh &amp; Swadeshi</span>
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9" />

        <div className="flex items-center gap-2">
          {auth?.role === "admin" ? (
            <SignedInPill
              testid="header-admin-signed-in"
              to="/admin"
              label={`Admin: ${auth.username}`}
              icon={ShieldCheck}
              onLogout={logout}
              variant="primary"
            />
          ) : (
            <a
              href="/admin/login"
              target="_blank"
              rel="noopener"
              data-testid="header-admin-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full border border-primary/30 text-primary text-xs font-semibold uppercase tracking-widest hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Admin
            </a>
          )}

          {auth?.role === "agent" ? (
            <SignedInPill
              testid="header-agent-signed-in"
              to="/agent"
              label={`Agent: ${auth.username}`}
              icon={UserCog}
              onLogout={logout}
              variant="accent"
            />
          ) : (
            <a
              href="/agent/login"
              target="_blank"
              rel="noopener"
              data-testid="header-agent-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full border border-accent/50 text-accent-foreground bg-accent/15 text-xs font-semibold uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <UserCog className="w-3.5 h-3.5" />
              Agent
            </a>
          )}

          <div className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-full border border-border bg-background/70 hover:bg-background focus-within:bg-background focus-within:border-primary transition-colors">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              data-testid="header-search-input"
              type="text"
              value={query}
              onChange={onSearch}
              placeholder='Search "haldi", "besan", "til"...'
              className="bg-transparent outline-none text-sm w-48 md:w-56 placeholder:text-muted-foreground/70"
            />
            {query && (
              <button
                data-testid="header-search-clear"
                onClick={() => onSearch({ target: { value: "" } })}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Bag</span>
            {totals.count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold flex items-center justify-center"
              >
                {totals.count}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((s) => !s)}
            className="md:hidden hidden p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background border-t border-border">
          <nav className="flex flex-col px-6 py-4 gap-3" />
        </div>
      )}
    </header>
  );
};

const SignedInPill = ({ testid, to, label, icon: Icon, onLogout, variant }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = () => setOpen(false);
    if (open) {
      document.addEventListener("click", close);
      return () => document.removeEventListener("click", close);
    }
  }, [open]);

  const base =
    variant === "accent"
      ? "bg-accent text-accent-foreground border-accent hover:bg-accent/90"
      : "bg-primary text-primary-foreground border-primary hover:bg-primary/90";

  return (
    <div className="relative hidden md:inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        data-testid={testid}
        onClick={() => setOpen((s) => !s)}
        className={`inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full border text-xs font-semibold uppercase tracking-widest transition-colors ${base}`}
      >
        <Icon className="w-3.5 h-3.5" />
        <span className="normal-case tracking-normal font-medium">{label}</span>
      </button>
      {open && (
        <div
          data-testid={`${testid}-menu`}
          className="absolute right-0 top-12 min-w-[180px] bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50"
        >
          <a
            href={to}
            target="_blank"
            rel="noopener"
            className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted"
          >
            <LayoutDashboard className="w-4 h-4" /> Open dashboard
          </a>
          <button
            data-testid={`${testid}-logout`}
            onClick={() => {
              onLogout();
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-muted text-destructive"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};
